import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

/**
 * Production's transient confirmation.
 *
 * The dashboard raises one after a check-in is saved and after a review is
 * submitted — `toast({ title: "Check-in saved" })` and
 * `toast({ title: "Thank you for your feedback!" })`. V2 had no equivalent, so
 * both writes completed in silence.
 *
 * **This is the smallest thing that reproduces that**, not a port of the
 * component library production uses. One title, one optional description, a
 * fixed dwell, and a dismiss. There is no variant, no action slot, and no
 * queue-length policy, because production's two calls use none of them.
 *
 * **It announces itself.** The region is a polite live region, so a confirmation
 * that only appears visually — which is what a bare styled div would be — still
 * reaches a screen reader. A confirmation nobody can perceive is not a
 * confirmation.
 */
export interface Toast {
  id: number;
  title: string;
  description?: string;
}

/** How long a toast stays up. Matches production's default dwell. */
export const TOAST_DURATION_MS = 5000;

interface ToastContextValue {
  toast: (input: { title: string; description?: string }) => void;
  toasts: Toast[];
  dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(1);
  /*
    Timers are held so unmounting clears them. Without this a toast raised just
    before a navigation would fire its removal into a component that is gone.
  */
  const timers = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());

  const dismiss = useCallback((id: number) => {
    const timer = timers.current.get(id);
    if (timer !== undefined) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
    setToasts((current) => current.filter((t) => t.id !== id));
  }, []);

  const toast = useCallback(
    ({ title, description }: { title: string; description?: string }) => {
      const id = nextId.current++;
      // Spread rather than assign: `exactOptionalPropertyTypes` distinguishes
      // an absent description from one explicitly set to undefined.
      setToasts((current) => [
        ...current,
        { id, title, ...(description === undefined ? {} : { description }) },
      ]);
      timers.current.set(
        id,
        setTimeout(() => dismiss(id), TOAST_DURATION_MS),
      );
    },
    [dismiss],
  );

  useEffect(() => {
    const held = timers.current;
    return () => {
      held.forEach(clearTimeout);
      held.clear();
    };
  }, []);

  const value = useMemo(() => ({ toast, toasts, dismiss }), [toast, toasts, dismiss]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <Toaster toasts={toasts} dismiss={dismiss} />
    </ToastContext.Provider>
  );
}

/**
 * Raise a toast.
 *
 * Outside a provider this is a no-op rather than a throw. A confirmation is not
 * worth taking a page down for, and every test that mounts one component in
 * isolation would otherwise have to wrap it.
 */
export function useToast(): ToastContextValue['toast'] {
  const context = useContext(ToastContext);
  return context?.toast ?? noop;
}

const noop = () => {};

function Toaster({ toasts, dismiss }: { toasts: Toast[]; dismiss: (id: number) => void }) {
  return (
    <div
      className="pointer-events-none fixed inset-x-0 bottom-0 z-50 flex flex-col items-center gap-2 p-4 sm:items-end"
      data-testid="toaster"
      role="status"
      aria-live="polite"
      aria-atomic="false"
    >
      {toasts.map((t) => (
        <div
          key={t.id}
          className="pointer-events-auto flex w-full max-w-sm items-start justify-between gap-3 rounded-lg border border-border bg-card p-4 shadow-lg"
          data-testid="toast"
        >
          <div className="flex min-w-0 flex-col gap-1">
            <p className="text-sm font-medium" data-testid="toast-title">
              {t.title}
            </p>
            {t.description ? (
              <p className="text-sm text-muted-foreground" data-testid="toast-description">
                {t.description}
              </p>
            ) : null}
          </div>

          <button
            type="button"
            onClick={() => dismiss(t.id)}
            data-testid="toast-dismiss"
            aria-label="Dismiss"
            className="shrink-0 rounded-md px-1 text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          >
            ×
          </button>
        </div>
      ))}
    </div>
  );
}
